import { ConnectedAppListItem } from "./useThirdPartySharing";
import { useCallback, useEffect, useMemo } from "react";
import { plural, t } from "@lingui/macro";
import { MenuListItemVariant } from "@WahooFitness/redesignr";
import {
  useDialogContext,
  useConfigContext,
  useNativeMessagingContext,
  postAuthorizeShareService,
  postDeauthorizeShareService,
  postRequestShareServiceStatus,
} from "@WahooFitness/wahoo-offline-mfe";
import { ChevronRight } from "@carbon/icons-react";
import Box from "@mui/system/Box";
import HealthConnectIcon from "@/assets/HealthConnect.png";
import AppleHealthIcon from "@/assets/AppleHealth.png";
import { DialogContentText, Typography } from "@mui/material";
import useRecoverySourceAutoSelect from "./useRecoverySourceAutoSelect";
import { clearPendingRecoverySourceAutoSelect } from "./recoverySourceAutoSelectPending";
import { formatMissingWriteTypeList } from "./missingWriteTypeCopy";
import useAppleHealthWorkoutImportEnabled from "./useAppleHealthWorkoutImportEnabled";
import {
  WAHOO_APP_ANDROID_FITNESS_APP_ID,
  WAHOO_APP_IOS_FITNESS_APP_ID,
} from "./wahooAppFitnessAppIds";

// The cloud's fitness_app_id for Apple Health, per environment. It has no connected_apps record, but
// the account's recovery_fitness_app_id has to point at a real cloud id for Apple Health to be the
// recovery source; the cloud rejects any id that isn't an existing fitness app. Health Connect has no
// such id, so it can't be a recovery source.
export const APPLE_HEALTH_FITNESS_APP_ID_BY_ENVIRONMENT: Record<"staging" | "production", number> =
  {
    staging: 1543,
    production: 3809,
  };

const useNativeHealth = () => {
  const { fitnessAppId, environment } = useConfigContext();
  const { nativeShareServiceStatus } = useNativeMessagingContext();
  const { id, name, icon, description, notEnabled, secondaryText } = useMemo(() => {
    if (fitnessAppId === WAHOO_APP_IOS_FITNESS_APP_ID) {
      return {
        id: "appleHealth",
        name: t`Apple Health`,
        icon: AppleHealthIcon,
        description: t`The Apple Health app gives you a common place to store all your health and fitness data.`,
        notEnabled: t`Privacy settings do not allow for data sharing. To change Apple Health permissions,
        open the Health app > Profile > Apps and turn on "Wahoo".`,
        secondaryText: undefined,
      };
    }
    if (fitnessAppId === WAHOO_APP_ANDROID_FITNESS_APP_ID) {
      return {
        id: "healthConnect",
        name: t`Health Connect`,
        icon: HealthConnectIcon,
        description: t`Health Connect stores your health and fitness data, giving you a simple way to sync the different apps on your phone.`,
        notEnabled: undefined,
        secondaryText: t`This will allow your data to be viewed in Google Fit`,
      };
    }
    return {
      id: undefined,
      name: undefined,
      icon: undefined,
      description: undefined,
      notEnabled: undefined,
      secondaryText: undefined,
    };
  }, [fitnessAppId]);

  const nativeHealthFitnessAppId =
    id === "appleHealth" ? APPLE_HEALTH_FITNESS_APP_ID_BY_ENVIRONMENT[environment] : undefined;

  useEffect(() => {
    if (id) {
      postRequestShareServiceStatus(id);
    }
  }, [id]);

  const { setDialog, handleClose } = useDialogContext();
  const { appleHealthWorkoutImportEnabled } = useAppleHealthWorkoutImportEnabled();
  const importsWorkouts = id === "appleHealth" && appleHealthWorkoutImportEnabled;
  const { markPendingAutoSelect } = useRecoverySourceAutoSelect();

  // Sharing is on and workouts still export, but some write permissions are missing, so those samples
  // are being dropped silently. See DEX-1189.
  const isIncomplete = useMemo(
    () => !!nativeShareServiceStatus?.capabilities?.includes("incompleteSharing"),
    [nativeShareServiceStatus?.capabilities]
  );

  // The missing permissions are already answered, so re-requesting them shows nothing — native sends
  // the user to the Health app instead, and the label has to match where they actually end up.
  const isIncompleteHealthAppOnly = useMemo(
    () => !!nativeShareServiceStatus?.capabilities?.includes("incompleteSharingHealthApp"),
    [nativeShareServiceStatus?.capabilities]
  );

  // Connected but unable to save workouts (Workouts or Workout Routes off) arrives as an authorized
  // status without `sharing`, so the capability list being non-empty is not proof workouts export.
  const isSharing = useMemo(
    () => !!nativeShareServiceStatus?.capabilities?.includes("sharing"),
    [nativeShareServiceStatus?.capabilities]
  );

  const isRecoveryDataReadable = useMemo(
    () => !!nativeShareServiceStatus?.capabilities?.includes("recoveryDataReadable"),
    [nativeShareServiceStatus?.capabilities]
  );

  // Names the specific data types behind `isIncomplete`, when native reports them, instead of the
  // generic message below.
  const missingWriteTypeList = useMemo(
    () => formatMissingWriteTypeList(nativeShareServiceStatus?.missingWriteTypeKeys),
    [nativeShareServiceStatus?.missingWriteTypeKeys]
  );
  const missingWriteTypeCount = nativeShareServiceStatus?.missingWriteTypeKeys?.length ?? 0;

  // `canBeRecoverySource` marks Apple Health for auto-selection as the account's first recovery
  // source before the native permission sheet opens; the outcome is settled by the caller once
  // native reports the service authorized.
  const handleNativeHealthDialog = useCallback(
    (canBeRecoverySource: boolean) => {
      if (!id) {
        return;
      }
      const authorizedOnly =
        nativeShareServiceStatus?.status === "authorized" &&
        nativeShareServiceStatus?.capabilities?.length === 0;
      setDialog({
        open: true,
        title: isIncomplete
          ? t`Turn on missing permissions?`
          : authorizedOnly
            ? t`Enable ${name}?`
            : t`Authorize ${name}?`,
        body: (
          <DialogContentText>
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              data-testid="native-health-dialog-body"
            >
              {isIncomplete ? (
                <Typography variant="prose-base">
                  {missingWriteTypeList
                    ? plural(missingWriteTypeCount, {
                        one: `${missingWriteTypeList} isn't being saved to ${name}. Turn every permission on the next screen on so your workouts save in full.`,
                        other: `${missingWriteTypeList} aren't being saved to ${name}. Turn every permission on the next screen on so your workouts save in full.`,
                      })
                    : t`Some workout data isn't being saved to ${name}. Turn every permission on the next screen on so your workouts save in full.`}
                </Typography>
              ) : (
                <Typography variant="prose-base">{description}</Typography>
              )}
              {!isIncomplete && importsWorkouts && (
                <Typography variant="prose-base">
                  {t`Wahoo saves your workouts to Apple Health, and adds workouts from other apps from the last 30 days.`}
                </Typography>
              )}
              {!isIncomplete && authorizedOnly && notEnabled && (
                <Typography variant="prose-base">{notEnabled}</Typography>
              )}
            </Box>
          </DialogContentText>
        ),
        actions: [
          { text: t`Cancel` },
          {
            text: isIncomplete ? t`Continue` : authorizedOnly ? t`Enable` : t`Authorize`,
            color: "info",
            action: () => {
              // A fresh connect only; fixing permissions on an authorized service is not one.
              if (
                canBeRecoverySource &&
                nativeHealthFitnessAppId !== undefined &&
                nativeShareServiceStatus?.status !== "authorized"
              ) {
                markPendingAutoSelect(nativeHealthFitnessAppId);
              }
              postAuthorizeShareService(id);
              handleClose();
            },
          },
        ],
      });
    },
    [
      isIncomplete,
      description,
      handleClose,
      id,
      importsWorkouts,
      markPendingAutoSelect,
      missingWriteTypeCount,
      missingWriteTypeList,
      name,
      nativeHealthFitnessAppId,
      nativeShareServiceStatus?.capabilities?.length,
      nativeShareServiceStatus?.status,
      notEnabled,
      setDialog,
    ]
  );

  const addNativeHealth = useCallback(
    (
      connectedApps: ConnectedAppListItem[] | undefined,
      {
        canBeRecoverySource = false,
        listAsAvailable = false,
      }: { canBeRecoverySource?: boolean; listAsAvailable?: boolean } = {}
    ) => {
      if (!connectedApps) {
        return;
      }
      if (id && ["authorized", "notAuthorized"].includes(nativeShareServiceStatus?.status ?? "")) {
        return [
          {
            // listAsAvailable keeps an authorized row tappable, so its dialog can re-request access.
            authorized:
              !listAsAvailable &&
              nativeShareServiceStatus?.status === "authorized" &&
              nativeShareServiceStatus.capabilities &&
              nativeShareServiceStatus.capabilities?.length > 0
                ? { share_summaries: isSharing, auto_share_summaries: isSharing }
                : null, // authorized, but we don't show the capabilities,
            nativeHealth: true,
            nativeHealthIncomplete: isIncomplete,
            nativeHealthIncompleteHealthAppOnly: isIncompleteHealthAppOnly,
            nativeHealthRecoveryDataReadable: isRecoveryDataReadable,
            nativeHealthMissingWriteTypeKeys: nativeShareServiceStatus?.missingWriteTypeKeys,
            fixNativeHealth: () => postAuthorizeShareService(id),
            // No connected_apps record to read capabilities from, so the recovery-source flag
            // stands in for import_vitals: it's what shows the "Recovery data source" row in
            // the unfiltered list, and STD users mustn't see that.
            supportedCapabilities: [
              ...(canBeRecoverySource && nativeHealthFitnessAppId !== undefined
                ? ["import_vitals" as const]
                : []),
              ...(importsWorkouts ? ["import_workouts" as const] : []),
            ],
            fitnessAppId: nativeHealthFitnessAppId,
            id,
            icon: (
              <Box
                overflow="hidden"
                borderRadius={2}
                border={1}
                borderColor="divider"
                height={24}
                width={24}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <img width="24" height="24" alt={name} src={icon} />
              </Box>
            ),
            content: name,
            // Left reading as authorized on purpose — sharing is on, it just isn't saving everything.
            secondaryContent: isIncomplete
              ? missingWriteTypeList
                ? plural(missingWriteTypeCount, {
                    one: `${missingWriteTypeList} isn't syncing. Tap to fix.`,
                    other: `${missingWriteTypeList} aren't syncing. Tap to fix.`,
                  })
                : t`Some data isn't being saved. Tap to fix.`
              : secondaryText,
            variant: MenuListItemVariant.Action,
            actionComponent: <ChevronRight size={24} />,
            action: () => handleNativeHealthDialog(canBeRecoverySource),
            // Fire-and-forget: the outcome arrives later through nativeShareServiceStatus, so
            // the post itself is the best success signal available here.
            handleDeauth: async () => {
              void clearPendingRecoverySourceAutoSelect();
              postDeauthorizeShareService(id);
              return true;
            },
          } as ConnectedAppListItem,
          ...connectedApps,
        ];
      } else {
        return connectedApps;
      }
    },
    [
      handleNativeHealthDialog,
      icon,
      id,
      importsWorkouts,
      isIncomplete,
      isIncompleteHealthAppOnly,
      isRecoveryDataReadable,
      isSharing,
      missingWriteTypeCount,
      missingWriteTypeList,
      name,
      nativeHealthFitnessAppId,
      nativeShareServiceStatus?.capabilities,
      nativeShareServiceStatus?.missingWriteTypeKeys,
      nativeShareServiceStatus?.status,
      secondaryText,
    ]
  );

  return { addNativeHealth, nativeHealthFitnessAppId };
};

export default useNativeHealth;
